import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useStore } from '../../../../store/store';
import { coursePageStyles } from './CoursePageStyle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CoursePageItemTitle from '../../../../components/titleAndInput/CoursePageItemTitle';
import Button from '@mui/material/Button';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { getPageStatusColor } from '../../../../utils/courseDashboard/getPageStatusColor';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import { CopyPageIcon, CopyPageSubpageIcon, DuplicatePageIcon, PersonPinCircleDashed } from '../../../../components/icons/Icons';
import useConfirm from '../../../../hooks/useConfirm';
import { Divider } from '@mui/material';
import { IPageItem } from './CoursePageList';
import { findNodeById } from '../../../../utils/courseDashboard/coureDashboardUtils';
import { v4 as uuidv4 } from 'uuid';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

interface ICoursePageItemProps {
  nodeData: IPageItem; // Node data passed from the tree
  onExpand: (e: any, key: string) => void;
  handleNewPageItem: (item: IPageItem) => void;
  handleCopyPageItem: (item: IPageItem, copyChildren: boolean) => void;
  handleDuplicatePageItem: (item: IPageItem) => void;
  handlePastePage: (item: IPageItem) => void;
  expandedKeys: string[];
  draggingKey: string;
  image?: boolean;
}

export default observer(function CoursePageItem({
  nodeData,
  handleNewPageItem,
  handleCopyPageItem,
  handleDuplicatePageItem,
  handlePastePage,
  onExpand,
  draggingKey,
  expandedKeys,
  image = false,
}: ICoursePageItemProps) {
  const { data: page, key: pageID, children } = nodeData;
  const { courseStore } = useStore();
  const { courseViewModel, pageProperties, removePage, getPageProperties } = courseStore;
  const minTitleWidth = 72;
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [onHover, setOnHover] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const theme = useTheme();
  const classes = coursePageStyles({ theme });

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [removePageWarning, setRemovePageWarning] = useState('Are you sure you want to delete this page?');

  const [pageHasLastVisit, setPageHasLastVisit] = useState(false);
  const history = useHistory();
  const [Dialog, confirmDelete] = useConfirm('Delete page', removePageWarning);

  const imageBaseURL = process.env.REACT_APP_ENV_BASEURL;
  const defaultAvatarUrl = 'https://niastest02.norskinteraktiv.no/niasAPI/img/thumb/DefaultCourseAvatar.jpg';
  const defaultAvatarName = 'defaultcourseavatar.jpg';
  const avatarValue = page.avatar?.value;
  const normalizedAvatarValue = typeof avatarValue === 'string' ? avatarValue.trim() : '';
  const resolvedAvatarUrl =
    normalizedAvatarValue && normalizedAvatarValue !== 'false'
      ? normalizedAvatarValue.includes('https')
        ? normalizedAvatarValue
        : 'https://' + imageBaseURL + '.norskinteraktiv.no/niasAPI' + normalizedAvatarValue
      : '';
  const hasImageUrl = !!resolvedAvatarUrl && !resolvedAvatarUrl.toLowerCase().includes(defaultAvatarName);
  const url = hasImageUrl ? resolvedAvatarUrl : defaultAvatarUrl;

  const goToEdit = () => {
    history.push(`/courseEdit?courseID=${courseViewModel != undefined ? courseViewModel.courseID : 0}&pageID=${pageID}`);
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>, isContext = false) => {
    e.stopPropagation();
    if (isContext) {
      e.preventDefault();
      setMenuAnchorEl(null);
      setMenuAnchorPosition({ top: e.clientY, left: e.clientX });
    } else {
      setMenuAnchorPosition(null);
      setMenuAnchorEl(e.currentTarget);
    }
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuAnchorPosition(null);
    setMenuOpen(false);
  };

  const handleMenuClick = (aCopyChildren: boolean) => {
    handleCopyPageItem(nodeData, aCopyChildren);
    handleCloseMenu();
  };

  const handleMenuDuplicateClick = () => {
    handleDuplicatePageItem(nodeData);
    handleCloseMenu();
  };

  const handleRemovePage = async (e: any) => {
    setMenuOpen(false);
    e.stopPropagation();
    if (children.length > 0) {
      // eslint-disable-next-line quotes
      setRemovePageWarning("Are you sure you want to delete this page and it's subpages?");
    }

    const result = await confirmDelete();
    if (result) {
      removePage(String(pageID));
    }
  };

  const handleOpenPageProperties = () => {
    if (courseViewModel) {
      getPageProperties(courseViewModel.courseID, parseInt(pageID.toString()));
    }
  };

  useEffect(() => {
    const expanded = expandedKeys.includes(pageID as string);
    const lastPageID = courseViewModel?.lastPageVisited ? courseViewModel?.lastPageVisited : '0';
    const lastPageItem = findNodeById(children, lastPageID);
    if (lastPageItem && !expanded) {
      setPageHasLastVisit(true);
    } else {
      setPageHasLastVisit(false);
    }
  }, [courseViewModel, expandedKeys]);

  return (
    <>
      <div
        className='pageItem'
        data-drag='true' //used to find the target for the ghost element in CoursePageList.tsx
        data-title-editing={isTitleEditing ? 'true' : 'false'}
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minWidth: 0,
          pointerEvents: 'all',
        }}
      >
        <Box
          onClick={handleOpenPageProperties}
          onDoubleClick={goToEdit}
          onContextMenu={(e) => handleOpenMenu(e, true)}
          display='flex'
          style={{
            justifyContent: 'space-between',
            height: image ? '70px' : '40px',
            backgroundColor: theme.palette.bgFour.main,
            borderRadius: 5,
            overflow: 'hidden',
            outline: '2px solid',
            color: theme.palette.bgFour.contrastText,
            outlineOffset: pageID === draggingKey ? 5 : 0,
            outlineColor: pageID === draggingKey ? theme.palette.secondary.main : pageID == pageProperties?.coursePageID ? theme.palette.primary.main : 'transparent',
            viewTransitionName: `page-bg-${pageID}`,
            width: '100%',
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: '1 1 0%',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {image && (
              <div
                style={{
                  maxWidth: 124,
                  minWidth: hasImageUrl ? 0 : 124,
                  height: '100%',
                  flex: '0 0 auto',
                  background: theme.palette.bgThree.main,
                  alignSelf: 'center',
                  marginRight: theme.spacing(1),
                  viewTransitionName: `page-image-${pageID}`,
                }}
              >
                <img style={{ height: '100%', width: hasImageUrl ? 'unset' : '100%', objectFit: 'cover' }} src={url} onLoad={() => setImgLoading(false)} />
              </div>
            )}
            <span data-drag-handle='true' style={{ display: 'flex', alignItems: 'center' }}>
              <DragIndicatorIcon
                fontSize='small'
                style={{
                  marginRight: theme.spacing(0.5),
                  color: theme.palette.darkGray.main,
                  cursor: 'grab',
                  flexShrink: 0,
                }}
              />
            </span>
            <div style={{ viewTransitionName: `page-title-${pageID}`, flex: '1 1 0%', minWidth: minTitleWidth, width: '100%', overflow: 'hidden' }}>
              <CoursePageItemTitle
                className={classes.title}
                data={page}
                coursePageID={String(pageID)}
                fontSize={image ? 20 : undefined}
                onFocus={handleOpenPageProperties}
                onEditModeChange={setIsTitleEditing}
              />
            </div>
          </div>
          <Box display='flex' alignSelf='flex-end' justifyContent='flex-end' alignItems='center' style={{ flex: '0 0 auto', height: '100%', paddingLeft: theme.spacing(0.5) }}>
            {pageHasLastVisit && (
              <Tooltip title='Last Visited Chapter' placement='top' tabIndex={1} arrow={true} classes={{ tooltip: classes.tooltip }} style={{ cursor: 'default' }}>
                <div onClick={(e) => onExpand(e, String(pageID))} className={classes.iconButton} style={{ display: 'flex', height: '20px', cursor: 'pointer' }}>
                  <PersonPinCircleDashed fontSize={'small'} />
                </div>
              </Tooltip>
            )}
            {courseViewModel?.lastPageVisited == String(pageID) && (
              <Tooltip title='Last Visited Page' placement='top' tabIndex={1} arrow={true} classes={{ tooltip: classes.tooltip }} style={{ cursor: 'default' }}>
                <PersonPinCircleIcon className={classes.iconButton} fontSize='small' style={{ viewTransitionName: `page-toolbar-pin-${pageID}` }} />
              </Tooltip>
            )}
            <Tooltip title='Page Properties' placement='top' tabIndex={1} arrow={true} classes={{ tooltip: classes.tooltip }}>
              <IconButton size='small' className={classes.iconButton} onClick={handleOpenPageProperties} style={{ viewTransitionName: `page-toolbar-settings-${pageID}` }}>
                <SettingsIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Edit Page' placement='top' tabIndex={1} arrow={true} classes={{ tooltip: classes.tooltip }}>
              <IconButton
                data-pagetour='3'
                size='small'
                className={classes.iconButton}
                component={Link}
                to={`/courseEdit?courseID=${courseViewModel != undefined ? courseViewModel.courseID : 0}&pageID=${pageID}`}
                style={{ viewTransitionName: `page-toolbar-edit-${pageID}` }}
              >
                <EditIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
            <Tooltip title='More' placement='top' tabIndex={1} arrow={true} classes={{ tooltip: classes.tooltip }}>
              <IconButton size='small' className={classes.iconButton} onClick={handleOpenMenu} style={{ viewTransitionName: `page-toolbar-more-${nodeData.key}` }}>
                <MoreHorizIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id='basic-menu'
              anchorEl={menuAnchorEl}
              anchorReference={menuAnchorPosition ? 'anchorPosition' : 'anchorEl'}
              anchorPosition={menuAnchorPosition ? { top: menuAnchorPosition.top, left: menuAnchorPosition.left } : undefined}
              open={menuOpen}
              onClose={handleCloseMenu}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem onClick={() => handleMenuDuplicateClick()}>
                <ListItemIcon>
                  <DuplicatePageIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Duplicate Page</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => handleMenuClick(false)}>
                <ListItemIcon>
                  <CopyPageIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Copy Page</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleMenuClick(true)}>
                <ListItemIcon>
                  <CopyPageSubpageIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Copy Page and Subpages</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={(e) => handleRemovePage(e)}>
                <ListItemIcon>
                  <DeleteIcon fontSize='medium' />
                </ListItemIcon>
                <ListItemText>Delete Page</ListItemText>
              </MenuItem>
            </Menu>
            <span
              style={{ minWidth: 10, maxWidth: 10, height: '100%', borderRadius: '0px 5px 5px 0px', background: getPageStatusColor(page), viewTransitionName: `page-toolbar-status-${pageID}` }}
            ></span>
          </Box>
        </Box>

        <div
          onMouseEnter={() => setOnHover(true)}
          onMouseLeave={() => setOnHover(false)}
          className={classes.plusBar}
          style={{ height: 6, display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', pointerEvents: draggingKey !== '' ? 'none' : 'auto' }}
        >
          {onHover && (
            <>
              <div className={classes.plusBarContainer} onClick={() => handleNewPageItem(nodeData)}>
                <div style={{ width: '100%', height: '100%' }}></div>
              </div>

              <ButtonGroup variant='contained' size='small' color='secondary' disableElevation aria-label='outlined primary button group' className={classes.plusBarButtonGroup}>
                <Tooltip title='Add New Page' placement='top' arrow={true} classes={{ tooltip: classes.tooltip }}>
                  <Button onClick={() => handleNewPageItem(nodeData)}>
                    <AddIcon fontSize='small' />
                  </Button>
                </Tooltip>
                <Tooltip title='Paste Page' placement='top' arrow={true} classes={{ tooltip: classes.tooltip }}>
                  <Button onClick={() => handlePastePage(nodeData)}>
                    <ContentPasteIcon fontSize='small' />
                  </Button>
                </Tooltip>
              </ButtonGroup>

              {/*             <div className={classes.plusBarStyleLeft}>
              <AddIcon fontSize='small' />
            </div>
            <div className={classes.plusBarStyleRight}>
              <ContentPasteIcon fontSize='small' style={{ fontSize: 15 }} />
            </div> */}
            </>
          )}
        </div>
      </div>

      <Dialog />
    </>
  );
});
